import {
  Box,
  Button,
  Heading,
  Input,
  Text,
  VStack,
  useToast,
  FormControl,
  FormErrorMessage,
  InputRightElement,
  FormLabel,
  InputGroup,
  Container,
  Stack,
  useColorModeValue,
  IconButton,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ResetPasswordApi } from "../../utils/api";
import { Field, Formik } from "formik";
import { BiHide, BiShow } from "react-icons/bi";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [show, setShow] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // Theme hooks
  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.900", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.400");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const themeColor = "#319795";
  const hoverColor = "teal.600";
  const inputBg = useColorModeValue("white", "gray.700");

  const handleSubmit = (value) => {
    ResetPasswordApi({ token, newPassword: value.password })
      .then((myres) => {
        toast({
          title: "Reset Password",
          description: `Password has been reset successfully`,
          status: "success",
          duration: 6000,
          isClosable: true,
          position: "top",
        });
        setTimeout(() => {
          navigate("/login");
        }, 1800);
      })
      .catch((err) => {
        toast({
          title: "Error",
          description: err.response?.data?.message
            ? err.response?.data.message
            : err.message,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      });
  };

  return (
    <Box
      w="100%"
      minH="100vh"
      bg={bg}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Container maxW="lg">
        <Stack spacing={8} mx="auto" maxW="lg" py={12} px={6}>
          <Stack align="center">
            <Heading fontSize="4xl" color={textColor} textAlign="center">
              Reset Password
            </Heading>
            <Text fontSize="lg" color={subTextColor} textAlign="center">
              Enter your new password below 🔐
            </Text>
          </Stack>
          <Box
            rounded="lg"
            bg={cardBg}
            boxShadow="lg"
            p={8}
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Formik
              initialValues={{
                password: "",
                confirmPassword: "",
              }}
              onSubmit={handleSubmit}
              validate={(values) => {
                const errors = {};

                if (values.password.length < 8) {
                  errors.password = "Password must contain at least 8 characters";
                }

                if (!values.confirmPassword) {
                  errors.confirmPassword = "Please confirm your password";
                } else if (values.password !== values.confirmPassword) {
                  errors.confirmPassword = "Passwords do not match";
                }

                return errors;
              }}
            >
              {({ handleSubmit, errors, touched }) => (
                <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                  <VStack spacing={6} align="flex-start">
                    {/* Password Field */}
                    <FormControl
                      isInvalid={!!errors.password && touched.password}
                    >
                      <FormLabel htmlFor="password" color={textColor}>
                        New Password
                      </FormLabel>
                      <InputGroup>
                        <Field
                          as={Input}
                          id="password"
                          name="password"
                          type={show ? "text" : "password"}
                          placeholder="Enter new password"
                          bg={inputBg}
                          borderColor={borderColor}
                          color={textColor}
                          _focus={{
                            borderColor: themeColor,
                            boxShadow: `0 0 0 1px ${themeColor}`,
                          }}
                        />
                        <InputRightElement h="full">
                          <IconButton
                            variant="ghost"
                            onClick={() => setShow(!show)}
                            icon={show ? <BiHide /> : <BiShow />}
                            aria-label={show ? "Hide password" : "Show password"}
                            color={subTextColor}
                            _hover={{ bg: "transparent", color: themeColor }}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>{errors.password}</FormErrorMessage>
                    </FormControl>

                    {/* Confirm Password Field */}
                    <FormControl
                      isInvalid={
                        !!errors.confirmPassword && touched.confirmPassword
                      }
                    >
                      <FormLabel htmlFor="confirmPassword" color={textColor}>
                        Confirm Password
                      </FormLabel>
                      <InputGroup>
                        <Field
                          as={Input}
                          id="confirmPassword"
                          name="confirmPassword"
                          type={show ? "text" : "password"}
                          placeholder="Confirm new password"
                          bg={inputBg}
                          borderColor={borderColor}
                          color={textColor}
                          _focus={{
                            borderColor: themeColor,
                            boxShadow: `0 0 0 1px ${themeColor}`,
                          }}
                        />
                        <InputRightElement h="full">
                          <IconButton
                            variant="ghost"
                            onClick={() => setShow(!show)}
                            icon={show ? <BiHide /> : <BiShow />}
                            aria-label={show ? "Hide password" : "Show password"}
                            color={subTextColor}
                            _hover={{ bg: "transparent", color: themeColor }}
                          />
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage>
                        {errors.confirmPassword}
                      </FormErrorMessage>
                    </FormControl>

                    <Button
                      type="submit"
                      bg={themeColor}
                      color="white"
                      _hover={{
                        bg: hoverColor,
                      }}
                      size="lg"
                      fontSize="md"
                      width="full"
                      mt={2}
                    >
                      Reset Password
                    </Button>
                  </VStack>
                </form>
              )}
            </Formik>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default ResetPassword;
